import './style.scss';

import cn from 'classnames';
import { UTCTimestamp } from 'lightweight-charts';
import isNil from 'lodash/isNil';
import React, { useCallback, useMemo, useState } from 'react';

import { useRelatedChartsEvent } from 'hooks/charts/useRelatedChartsEvent';
import { useTypedSelector } from 'store';
import { ICursorPosition, ILogicalRange } from 'types/charts';
import { chartFormatter } from 'utils/charts';
import { EExchange } from 'web3';

import { formatPairTooltipVolume } from './formatVolume';

interface IPairTooltipProps {
  chartId: string;
  relatedCharts?: string[];
  isConverted?: boolean;
  exchange: EExchange;
  candles: {
    time: UTCTimestamp;
    close: number;
    high: number;
    low: number;
    open: number;
  }[];
  cursorEvent: string;
  logicalEvent: string;
  volumes: {
    time: UTCTimestamp;
    value: number;
  }[];
  internal_volumes?: {
    time: UTCTimestamp;
    value: number;
  }[];
}

const PairTooltip: React.FC<IPairTooltipProps> = ({
  chartId,
  relatedCharts,
  isConverted = false,
  exchange,
  candles,
  volumes,
  internal_volumes,
  cursorEvent,
  logicalEvent,
}) => {
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const [visibleLogicalRange, _setVisibleLogicalRange] = useState<ILogicalRange>(undefined);
  const onLogicalRangeChanged = useCallback((p: any) => {
    _setVisibleLogicalRange(p);
  }, []);

  const [cursorPosition, _setCursorPosition] = useState<ICursorPosition>(undefined);

  useRelatedChartsEvent({
    events: [`${chartId}-${cursorEvent}`].concat(
      (relatedCharts ?? []).map(ch => `${ch}-${cursorEvent}`),
    ),
    handler: (v: unknown) => _setCursorPosition(v as unknown as any),
  });

  useRelatedChartsEvent({
    events: [`${chartId}-${logicalEvent}`].concat(
      (relatedCharts ?? []).map(ch => `${ch}-${logicalEvent}`),
    ),
    handler: (v: unknown) => onLogicalRangeChanged(v as unknown as any),
  });

  const firstVisibleIndex = useMemo(
    () => (visibleLogicalRange ? Math.ceil(visibleLogicalRange.from) : undefined),
    [visibleLogicalRange],
  );
  const lastVisibleIndex = useMemo(
    () => (visibleLogicalRange ? Math.floor(visibleLogicalRange.to) : undefined),
    [visibleLogicalRange],
  );

  const hoveredIndex = useMemo(() => {
    const result = cursorPosition
      ? cursorPosition === undefined
        ? lastVisibleIndex
        : cursorPosition.logical
      : lastVisibleIndex;

    const recordsLength = candles.length;

    if (!result) return recordsLength - 1;

    return result > recordsLength - 1 ? recordsLength - 1 : result;
  }, [lastVisibleIndex, cursorPosition, candles]);

  const high = useMemo(() => candles[hoveredIndex]?.high ?? 0, [candles, hoveredIndex]);

  const open = useMemo(() => candles[hoveredIndex]?.open ?? 0, [candles, hoveredIndex]);

  const low = useMemo(() => candles[hoveredIndex]?.low ?? 0, [candles, hoveredIndex]);

  const close = useMemo(() => candles[hoveredIndex]?.close ?? 0, [candles, hoveredIndex]);

  const volumeTotal = useMemo(() => volumes[hoveredIndex]?.value ?? 0, [volumes, hoveredIndex]);

  const volumeInternal = useMemo(
    () => (internal_volumes ? (internal_volumes[hoveredIndex]?.value ?? 0) : undefined),
    [internal_volumes, hoveredIndex],
  );

  const startPrice = useMemo(
    () => (firstVisibleIndex !== undefined ? (candles[firstVisibleIndex]?.close ?? 0) : undefined),
    [candles, firstVisibleIndex],
  );

  const isGreen = useMemo(() => close >= open, [close, open]);

  const amountCN = useMemo(() => cn('amount', { green: isGreen, red: !isGreen }), [isGreen]);

  const diff = useMemo(
    () => (startPrice !== undefined ? close - startPrice : undefined),
    [close, startPrice],
  );

  const diffPercentage = useMemo(
    () =>
      startPrice !== undefined && startPrice !== 0 && diff !== undefined
        ? Number(Math.abs((diff / startPrice) * 100).toFixed(2))
        : undefined,
    [startPrice, diff],
  );

  const totalVolumeString = useMemo(() => {
    return formatPairTooltipVolume(
      volumeTotal,
      exchange === EExchange.dex ? (isConverted ? '$' : '') : '',
    );
  }, [volumeTotal, isConverted, exchange]);

  const internalVolumeString = useMemo(() => {
    if (!volumeInternal) return '0';

    return formatPairTooltipVolume(
      volumeInternal,
      exchange === EExchange.dex ? (isConverted ? '$' : '') : '',
    );
  }, [volumeInternal, isConverted, exchange]);

  return (
    <div className="mm-pair-pair-chart-tooltip">
      <div className="pair-values">
        <div className="value">
          <span className="caption">O</span>
          <span className={amountCN}>{chartFormatter(open)}</span>
        </div>
        <div className="value">
          <span className="caption">H</span>
          <span className={amountCN}>{chartFormatter(high)}</span>
        </div>
        <div className="value">
          <span className="caption">L</span>
          <span className={amountCN}>{chartFormatter(low)}</span>
        </div>
        <div className="value">
          <span className="caption">C</span>
          <span className={amountCN}>{chartFormatter(close)}</span>
        </div>
        {diff !== undefined && diffPercentage !== undefined && (
          <div className="value">
            <span className={cn('amount', { green: diff >= 0, red: diff < 0 })}>
              {diff > 0 ? '+' : ''}
              {chartFormatter(diff)}
            </span>
            <span className={cn('amount', { green: diff >= 0, red: diff < 0 })}>
              ({diff >= 0 ? '+' : '-'}
              {diffPercentage}%)
            </span>
          </div>
        )}
      </div>
      {!isNil(volumeInternal) && isAdmin && (
        <div className="pair-values">
          <div className="volume-value">
            <span className="caption">Internal v:</span>
            <span className="amount">{internalVolumeString}</span>
          </div>
        </div>
      )}
      <div className="pair-values">
        <div className="volume-value">
          <span className="caption">Total v:</span>
          <span className="amount">{totalVolumeString}</span>
        </div>
      </div>
    </div>
  );
};

export { PairTooltip };
